// src/AdminApplications.js
import React, { useState, useEffect } from 'react';
import { textdb } from '../../../firebase'; // Adjust path if needed
import { collection, getDocs, doc, deleteDoc, writeBatch } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import styles from './AdminApplications.module.css';

const AdminApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchApplications = async () => {
    setLoading(true);
    setError(null);
    try {
      const querySnapshot = await getDocs(collection(textdb, "jobApplications"));
      const appsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      appsData.sort((a, b) => {
        const timeA = a.appliedAt?.toDate ? a.appliedAt.toDate() : 0;
        const timeB = b.appliedAt?.toDate ? b.appliedAt.toDate() : 0;
        return timeB - timeA;
      });
      setApplications(appsData);
    } catch (err) {
      console.error("Error fetching applications: ", err);
      setError("Failed to load applications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleDeleteOne = async (id) => {
    if (window.confirm("Are you sure you want to delete this application?")) {
      try {
        await deleteDoc(doc(textdb, "jobApplications", id));
        // Note: Deleting from Firestore doesn't delete the file from Storage.
        // You might want to implement that if needed, using the resumeURL/resumeFileName.
        setApplications(prevApps => prevApps.filter(app => app.id !== id));
        alert("Application deleted successfully.");
      } catch (err) {
        console.error("Error deleting application: ", err);
        alert("Failed to delete application.");
      }
    }
  };

  const handleDeleteAll = async () => {
    if (window.confirm("ARE YOU SURE you want to delete ALL applications? This action cannot be undone.")) {
      if (applications.length === 0) {
        alert("No applications to delete.");
        return;
      }
      try {
        const batch = writeBatch(textdb);
        applications.forEach(app => {
          const docRef = doc(textdb, "jobApplications", app.id);
          batch.delete(docRef);
          // Again, files in Storage are not deleted by this.
        });
        await batch.commit();
        setApplications([]);
        alert("All applications deleted successfully.");
      } catch (err) {
        console.error("Error deleting all applications: ", err);
        alert("Failed to delete all applications.");
      }
    }
  };

  const handleDownloadExcel = () => {
    if (applications.length === 0) {
      alert("No data to export.");
      return;
    }
    const dataToExport = applications.map(app => ({
      'Job Title': app.jobTitle,
      'Full Name': app.fullName,
      'WhatsApp Number': app.whatsappNumber,
      'Email': app.email,
      'About Yourself': app.aboutYourself,
      'Resume File': app.resumeFileName || 'N/A',
      'Resume Link': app.resumeURL || 'N/A',
      'Applied At': app.appliedAt?.toDate ? app.appliedAt.toDate().toLocaleString() : 'N/A'
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "JobApplications");
    XLSX.writeFile(workbook, "JobApplications.xlsx");
  };

  if (loading) return <div className={styles.message}>Loading applications...</div>;
  if (error) return <div className={`${styles.message} ${styles.error}`}>{error} <button onClick={fetchApplications} className={styles.retryButton}>Retry</button></div>;

  return (
    <div className={styles.adminContainer}>
      <h1>Job Applications</h1>
      <div className={styles.actions}>
        <button onClick={handleDownloadExcel} className={styles.actionButton}>Download as Excel</button>
        <button onClick={handleDeleteAll} className={`${styles.actionButton} ${styles.dangerButton}`}>Delete All Applications</button>
        <button onClick={fetchApplications} className={styles.actionButton}>Refresh Data</button>
      </div>

      {applications.length === 0 ? (
        <p className={styles.message}>No applications found.</p>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.applicationsTable}>
            <thead>
              <tr>
                <th>Job Title</th>
                <th>Full Name</th>
                <th>WhatsApp</th>
                <th>Email</th>
                <th>About</th>
                <th>Resume</th>
                <th>Applied At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {applications.map(app => (
                <tr key={app.id}>
                  <td>{app.jobTitle}</td>
                  <td>{app.fullName}</td>
                  <td>{app.whatsappNumber}</td>
                  <td>{app.email}</td>
                  <td className={styles.aboutCell} title={app.aboutYourself}>{app.aboutYourself}</td>
                  <td>
                    {app.resumeURL ? (
                      <a href={app.resumeURL} target="_blank" rel="noopener noreferrer" className={styles.resumeLink}>
                        {app.resumeFileName || 'View Resume'}
                      </a>
                    ) : (
                      'No Resume'
                    )}
                  </td>
                  <td>{app.appliedAt?.toDate ? app.appliedAt.toDate().toLocaleString() : 'N/A'}</td>
                  <td>
                    <button onClick={() => handleDeleteOne(app.id)} className={styles.deleteButton}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminApplications;